import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import useAppStore from '../store/appStore';

// Hilfskomponenten — Desktop-Rail: feste Breite 7.5rem (gleich Betty)

const ControlButton = ({ onClick, children, className = "", title }: any) => (
    <button
        type="button"
        onClick={onClick}
        title={title}
        className={`w-10 h-10 flex-1 flex items-center justify-center text-xl font-bold hover:bg-gray-700/50 transition-colors text-slate-300 hover:text-white ${className}`}
    >
        {children}
    </button>
);
const ControlSpacer = ({ children, horizontal = false }: { children?: React.ReactNode; horizontal?: boolean }) => (
    <div
        className={`${
            horizontal
                ? 'px-1.5 h-10 min-w-[2rem] flex-[0.85] border-x'
                : 'w-full py-1 border-y min-h-[28px]'
        } text-center text-[10px] font-semibold bg-[#0f1015] border-white/10 flex items-center justify-center text-slate-400 cursor-default`}
    >
        {children}
    </div>
);
const ControlPill = ({
    children,
    horizontal = false,
    className = '',
}: {
    children: React.ReactNode;
    horizontal?: boolean;
    className?: string;
}) => (
    <div
        className={`${
            horizontal ? 'flex-row rounded-full w-full' : 'flex-col rounded-full w-full h-full min-h-[6.5rem]'
        } flex items-center bg-[#15171e] text-white shadow-[0_0_15px_rgba(0,0,0,0.5)] border border-white/10 overflow-hidden ${className}`}
    >
        {children}
    </div>
);

/** Nur Elemente mit echtem Overflow als Scroll-Ziel nutzen (sonst ist der Button tot). */
const canElementScroll = (el: HTMLElement | null): el is HTMLElement => {
    if (!el) return false;
    const { overflowY } = window.getComputedStyle(el);
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
    return el.scrollHeight > el.clientHeight + 1 || el.scrollTop > 0;
};

const documentScrollHeight = () => {
    const body = document.body;
    const html = document.documentElement;
    return Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
    );
};

const ZoomControls: React.FC = () => {
    // ZOOM
    const { zoomLevel, setZoomLevel } = useAppStore(state => ({ zoomLevel: state.zoomLevel, setZoomLevel: state.setZoomLevel }));
    const handleZoomIn = () => setZoomLevel(prev => Math.min(150, prev + 10));
    const handleZoomOut = () => setZoomLevel(prev => Math.max(50, prev - 10));

    // SCROLL
    const [scrollState, setScrollState] = useState<'top' | 'middle' | 'bottom'>('top');
    const [isScrollable, setIsScrollable] = useState(false);
    const [targetElement, setTargetElement] = useState<HTMLElement | Window | null>(null);

    // 1. ZIEL FINDEN: nur wirklich scrollbare Container, sonst Window
    useEffect(() => {
        const findTarget = () => {
            const pageScroll = document.getElementById('main-scroll-container');
            const appMain = document.getElementById('app-main-scroll');

            let newTarget: HTMLElement | Window = window;
            if (canElementScroll(pageScroll)) {
                newTarget = pageScroll;
            } else if (canElementScroll(appMain)) {
                newTarget = appMain;
            }

            setTargetElement(prev => (prev !== newTarget ? newTarget : prev));
        };
        const timer = setInterval(findTarget, 500);
        findTarget();
        return () => clearInterval(timer);
    }, []);

    // 2. LOGIK & BEOBACHTUNG
    useEffect(() => {
        if (!targetElement) return;

        const updateState = () => {
            let scrollTop = 0, clientHeight = 0, scrollHeight = 0;

            if (targetElement === window) {
                scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
                clientHeight = window.innerHeight;
                scrollHeight = documentScrollHeight();
            } else {
                const el = targetElement as HTMLElement;
                scrollTop = el.scrollTop;
                clientHeight = el.clientHeight;
                scrollHeight = el.scrollHeight;
            }

            const hasScrolled = Math.round(scrollTop) > 0;
            const contentIsLonger = Math.round(scrollHeight) > Math.round(clientHeight) + 1;

            setIsScrollable(hasScrolled || contentIsLonger);

            if (scrollTop < 20) setScrollState('top');
            else if (scrollTop + clientHeight >= scrollHeight - 20) setScrollState('bottom');
            else setScrollState('middle');
        };

        targetElement.addEventListener('scroll', updateState);
        window.addEventListener('resize', updateState);
        // Document-Scroll (wenn Ziel = Window) oft am document, nicht nur am window
        if (targetElement === window) {
            document.addEventListener('scroll', updateState, true);
        }

        let resizeObserver: ResizeObserver | null = null;
        if (targetElement !== window) {
            resizeObserver = new ResizeObserver(() => {
                updateState();
            });
            resizeObserver.observe(targetElement as HTMLElement);
            if ((targetElement as HTMLElement).firstElementChild) {
                 resizeObserver.observe((targetElement as HTMLElement).firstElementChild!);
            }
        }

        const intervalId = setInterval(updateState, 1000);
        updateState();

        return () => {
            targetElement.removeEventListener('scroll', updateState);
            window.removeEventListener('resize', updateState);
            if (targetElement === window) {
                document.removeEventListener('scroll', updateState, true);
            }
            if (resizeObserver) resizeObserver.disconnect();
            clearInterval(intervalId);
        };
    }, [targetElement]);

    // FIX: Scrollt jetzt sowohl nach OBEN als auch nach LINKS zum Seitenanfang
    const scrollToTop = () => {
        if (!targetElement) return;
        if (targetElement === window) {
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        } else {
            (targetElement as HTMLElement).scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
    };

    const scrollToBottom = () => {
        if (!targetElement) return;
        if (targetElement === window) {
            window.scrollTo({ top: documentScrollHeight(), left: 0, behavior: 'smooth' });
        } else {
            const el = targetElement as HTMLElement;
            el.scrollTo({ top: el.scrollHeight, left: 0, behavior: 'smooth' });
        }
    };

    const IconUp = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6"/></svg>;
    const IconDown = <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>;

    return createPortal(
        <>
            {/* Mobil: schmale Leiste am unteren Bildrand — volle Breite für den Content darüber */}
            <div className="md:hidden fixed inset-x-0 bottom-0 z-[9998] h-14 no-screenshot print-hidden bg-[#0b0c10]/95 border-t border-white/10 backdrop-blur-md pointer-events-none" />
            <div
                className="fixed z-[9999] no-screenshot print-hidden pointer-events-auto
                inset-x-0 bottom-0 h-14 px-3 grid grid-cols-3 items-center
                md:inset-auto md:right-4 md:bottom-[30px] md:h-auto md:px-0
                md:flex md:flex-row md:items-stretch md:gap-2 md:grid-cols-none md:w-[7.5rem]"
            >
                {/* Mobil: Zoom rechts quer; Desktop: Zoom + Scroll hochkant nebeneinander (Summe = Betty-Breite) */}
                <div
                    className={`col-start-3 justify-self-end md:col-auto md:min-w-0 md:h-full ${
                        isScrollable ? 'md:flex-1' : 'md:w-full'
                    }`}
                >
                    {/* Mobil: horizontal */}
                    <div className="md:hidden">
                        <ControlPill horizontal>
                            <ControlButton onClick={handleZoomOut} className="!w-9 !h-9 rounded-l-full" title="Verkleinern">
                                -
                            </ControlButton>
                            <ControlSpacer horizontal>{zoomLevel}%</ControlSpacer>
                            <ControlButton onClick={handleZoomIn} className="!w-9 !h-9 rounded-r-full" title="Vergrößern">
                                +
                            </ControlButton>
                        </ControlPill>
                    </div>
                    {/* Desktop: vertikal / hochkant */}
                    <div className="hidden md:block h-full">
                        <ControlPill>
                            <ControlButton onClick={handleZoomIn} className="!w-full !h-9 !flex-none rounded-t-full" title="Vergrößern">
                                +
                            </ControlButton>
                            <ControlSpacer>{zoomLevel}%</ControlSpacer>
                            <ControlButton onClick={handleZoomOut} className="!w-full !h-9 !flex-none rounded-b-full" title="Verkleinern">
                                -
                            </ControlButton>
                        </ControlPill>
                    </div>
                </div>
                <div className="col-start-2 justify-self-center md:hidden" aria-hidden />
                <div className="col-start-1 justify-self-start md:col-auto md:flex-1 md:min-w-0 md:h-full">
                    {isScrollable ? (
                        <>
                            <div className="md:hidden">
                                <ControlPill horizontal>
                                    {(scrollState === 'middle' || scrollState === 'bottom') && (
                                        <ControlButton
                                            onClick={scrollToTop}
                                            className="!w-9 !h-9 rounded-l-full"
                                            title="Zum Anfang"
                                        >
                                            {IconUp}
                                        </ControlButton>
                                    )}
                                    {scrollState === 'middle' && (
                                        <ControlSpacer horizontal>
                                            <div className="w-1 h-1 rounded-full bg-slate-500" />
                                        </ControlSpacer>
                                    )}
                                    {(scrollState === 'top' || scrollState === 'middle') && (
                                        <ControlButton
                                            onClick={scrollToBottom}
                                            className={`!w-9 !h-9 ${scrollState === 'top' ? 'rounded-full' : 'rounded-r-full'}`}
                                            title="Nach unten"
                                        >
                                            {IconDown}
                                        </ControlButton>
                                    )}
                                </ControlPill>
                            </div>
                            <div className="hidden md:block h-full">
                                <ControlPill>
                                    {(scrollState === 'middle' || scrollState === 'bottom') && (
                                        <ControlButton
                                            onClick={scrollToTop}
                                            className="!w-full !h-9 !flex-none rounded-t-full"
                                            title="Zum Anfang"
                                        >
                                            {IconUp}
                                        </ControlButton>
                                    )}
                                    {scrollState === 'middle' && (
                                        <ControlSpacer>
                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                                        </ControlSpacer>
                                    )}
                                    {(scrollState === 'top' || scrollState === 'middle') && (
                                        <ControlButton
                                            onClick={scrollToBottom}
                                            className={`!w-full !h-9 !flex-none ${
                                                scrollState === 'top' ? 'rounded-full' : 'rounded-b-full'
                                            }`}
                                            title="Nach unten"
                                        >
                                            {IconDown}
                                        </ControlButton>
                                    )}
                                    {scrollState === 'bottom' && <ControlSpacer />}
                                </ControlPill>
                            </div>
                        </>
                    ) : (
                        <span className="md:hidden" />
                    )}
                </div>
            </div>
        </>,
        document.body
    );
};

export default ZoomControls;
